// Device reference for the public /submit-claim page.
//
// SECURITY NOTE: the reference is generated CLIENT-SIDE and base64url is
// encoding, not encryption — it can be decoded and forged by anyone. Treat it
// strictly as a correlation/tracking identifier. The backend must record the
// request IP, derive IP-geolocation and validate the payload independently;
// never trust this value for identity or authorization.

let fallbackNonceCounter = 0

function randomNonce() {
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  // the nonce only provides uniqueness (not secrecy), so a timestamp plus a
  // counter is a sufficient non-cryptographic fallback
  fallbackNonceCounter += 1
  return `${Date.now().toString(36)}-${fallbackNonceCounter.toString(36)}`
}

export function collectDeviceDetails() {
  const nav = window.navigator || {}
  const screen = window.screen || {}
  let timezone = ''
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    // Intl can be unavailable in exotic embedders; an empty timezone is a
    // valid value for the backend
    timezone = ''
  }
  return {
    nonce: randomNonce(),
    collectedAt: new Date().toISOString(),
    userAgent: nav.userAgent || '',
    platform: nav.platform || '',
    language: nav.language || '',
    languages: nav.languages ? Array.from(nav.languages) : [],
    vendor: nav.vendor || '',
    touchPoints: nav.maxTouchPoints || 0,
    cookiesEnabled: !!nav.cookieEnabled,
    online: !!nav.onLine,
    deviceMemory: nav.deviceMemory || null,
    hardwareConcurrency: nav.hardwareConcurrency || null,
    screen: {
      width: screen.width || 0,
      height: screen.height || 0,
      pixelRatio: window.devicePixelRatio || 1,
      colorDepth: screen.colorDepth || null,
    },
    timezone,
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    referrer: document.referrer || '',
  }
}

// UTF-8 <-> binary-string helpers replacing the deprecated escape()/
// unescape() pair around btoa()/atob()
function utf8ToBinary(text) {
  return encodeURIComponent(text).replaceAll(/%([0-9A-F]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  )
}

function binaryToUtf8(binary) {
  let percentEncoded = ''
  for (let i = 0; i < binary.length; i++) {
    percentEncoded += `%${binary.charCodeAt(i).toString(16).padStart(2, '0')}`
  }
  return decodeURIComponent(percentEncoded)
}

export function encodeDeviceRef(details) {
  const json = JSON.stringify(details)
  // base64url so the value is path-parameter safe; '=' only ever appears in
  // base64 output as trailing padding, so replaceAll is exact
  return btoa(utf8ToBinary(json))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '')
}

export function decodeDeviceRef(ref) {
  try {
    let base64 = ref.replaceAll('-', '+').replaceAll('_', '/')
    while (base64.length % 4) base64 += '='
    return JSON.parse(binaryToUtf8(atob(base64)))
  } catch {
    // forged or truncated refs are expected input on a public URL; null
    // tells the caller the ref carries no usable details
    return null
  }
}

// Resolves with {latitude, longitude, accuracy} or null — never rejects, so a
// denied/unavailable prompt cannot block claim submission.
export function getGeolocation(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          capturedAt: new Date(position.timestamp).toISOString(),
        }),
      () => resolve(null),
      { timeout: timeoutMs, maximumAge: 60000, enableHighAccuracy: false }
    )
  })
}
